import { isChunkLoadError, handleChunkLoadError } from "@/helpers/chunkError";
import RootLayout from "@/layout/RootLayout/RootLayout";
import { Navigation } from "@/components/Navigation";
import { ErrorComponentProps, useLocation } from "@tanstack/react-router";
import { DialogProvider } from "@vintecc/components";
import { useState, useEffect } from "react";
import { ChunkErrorBoundary } from "../ChunkErrorBoundary/ChunkErrorBoundary";
import { ErrorDisplay } from "../ErrorDisplay/ErrorDisplay";
import { Loader } from "../Loader/Loader";
import { Topbar } from "../Topbar";

export const RootErrorComponent = (props: ErrorComponentProps) => {
  const [isReloading, setIsReloading] = useState(false);
  const [reloadAttempted, setReloadAttempted] = useState(false);
  const { pathname } = useLocation();

  const moduleSlug = pathname.split("/").filter(Boolean)[0] ?? "root";

  useEffect(() => {
    if (isChunkLoadError(props.error) && !reloadAttempted) {
      const reloading = handleChunkLoadError();
      setIsReloading(reloading);
      setReloadAttempted(true);
    }
  }, [props.error, reloadAttempted]);

  if (isChunkLoadError(props.error)) {
    // While a chunk-load error is being handled (and a reload is in progress),
    // show a loader. If a reload has already been attempted this session and
    // won't be triggered again, show a friendly prompt instead.
    if (isReloading || !reloadAttempted) {
      return (
        <div className="w-full h-full flex items-center justify-center">
          <Loader size="md" />
        </div>
      );
    }

    // Auto-reload already attempted this session — surface a friendly prompt
    // so the user can manually reload rather than seeing a generic error.
    return (
      <DialogProvider>
        <RootLayout
          top={<Topbar />}
          nav={<Navigation />}
          main={<ChunkErrorBoundary />}
          mainTestId="route-error"
          moduleTestId={`module-${moduleSlug}`}
        />
      </DialogProvider>
    );
  }

  return (
    <DialogProvider>
      <RootLayout
        top={<Topbar />}
        nav={<Navigation />}
        main={<ErrorDisplay error={props} />}
        mainTestId="route-error"
        moduleTestId={`module-${moduleSlug}`}
      />
    </DialogProvider>
  );
};
